import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import { formatPrice } from '@/components/ui/Price';
import DishCodeTag from '@/features/menu/DishCodeTag';
import { selectWaiterCartLines, setWaiterLineQty } from '@/features/waiter/waiterCartSlice';
import { useModalBackClose } from './useModalBackClose';
import { diagnoseWaiterCart, staleLineMessage } from './staleCart';
import type { CartLine, MenuItem, OptionGroup } from '@/types';

export interface TicketCartModalProps {
  sessionUuid: string;
  restaurantUuid: string;
  /** DF-0106 — the note's kind decides which availability flag the fresh
   *  menu is checked against (sur_place / emporter). */
  kind: 'dine_in' | 'takeaway';
  /** Overlay tap / ✕ / Escape / Android back — old behaviour kept (W-T-35).
   *  TicketPage decides whether this closes the cart or, if the options
   *  modal is stacked on top of it, that one first. */
  onClose: () => void;
  /** Tapping a line's name — opens the options modal for THAT line. */
  onEditLine: (lineId: string) => void;
}

interface OptionSubLine {
  optionId: string;
  text: string;
}

/** Denormalizes a line's `selectedOptions` (ids + qty only) into display
 *  labels via the product's resolved option groups — same idea as
 *  `CartLineItem.tsx`'s `resolveOptionLabels`, duplicated rather than
 *  imported: that helper isn't exported and the guest cart module is out of
 *  scope for this feature (mirrors `waiterCartSlice.ts`'s own
 *  `waiterLineMergeKey` duplication for the same reason). */
function resolveOptionSubLines(line: CartLine, groups: OptionGroup[]): OptionSubLine[] {
  const subLines: OptionSubLine[] = [];
  for (const sel of line.selectedOptions) {
    const group = groups.find((g) => g.id === sel.groupId);
    if (!group) continue;
    for (const entry of sel.options) {
      const choice = group.options.find((o) => o.id === entry.id);
      if (!choice) continue;
      const qtySuffix = entry.qty > 1 ? ` ×${entry.qty}` : '';
      const priceSuffix =
        choice.priceDelta > 0 ? ` +${formatPrice(choice.priceDelta * entry.qty)}` : '';
      subLines.push({ optionId: entry.id, text: `${choice.label}${qtySuffix}${priceSuffix}` });
    }
  }
  return subLines;
}

/**
 * 04-ux-mobile-spec.md §2.3, 06-test-cases.md W-T-31..36 — the cart sheet
 * reached from the Plats footer's 🛒. Deliberately carries NO aggregate
 * total (W-T-31 pins "no element with Total inside cart-modal" — the
 * running total already lives in the always-visible `plats-total`; showing
 * it twice is what the old app's box, at `w-96` on a 390px phone, never had
 * room for either). Steppers mutate the tapped line directly — `+`/`−` here
 * never create or merge lines the way the Plats row `+` does; they only
 * change that ONE line's qty, removing it at 0 (W-T-32/33).
 */
export default function TicketCartModal({
  sessionUuid,
  restaurantUuid,
  kind,
  onClose,
  onEditLine,
}: TicketCartModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cartLines = useAppSelector(selectWaiterCartLines(sessionUuid));
  // DF-0106 — re-read the menu every time the sheet opens, like the guest's
  // CartDrawer: a required group created mid-service must show up here as
  // « À choisir » under the line, not as a 400 when the round is sent.
  const { data: menu } = useGetMenuQuery(restaurantUuid, {
    skip: !restaurantUuid,
    refetchOnMountOrArgChange: true,
  });

  useModalBackClose('cart-modal', onClose);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // W-T-36 — emptying the last line closes the sheet. Guarded so opening the
  // sheet on an already-empty cart (harmless, the 🛒 button has no such
  // gate) does not immediately close itself.
  const hadLines = useRef(cartLines.length > 0);
  useEffect(() => {
    if (hadLines.current && cartLines.length === 0) onClose();
    hadLines.current = cartLines.length > 0;
  }, [cartLines.length, onClose]);

  const itemsById = useMemo(() => {
    const map = new Map<string, MenuItem>();
    for (const item of menu?.items ?? []) map.set(item.id, item);
    return map;
  }, [menu]);

  const staleById = useMemo(
    () => new Map(diagnoseWaiterCart(cartLines, menu, kind).map((s) => [s.lineId, s])),
    [cartLines, menu, kind],
  );

  function step(line: CartLine, delta: number) {
    dispatch(setWaiterLineQty({ sessionUuid, lineId: line.lineId, qty: line.qty + delta }));
  }

  return (
    <div
      data-testid="cart-overlay"
      className="fixed inset-0 z-[1150] flex items-center justify-center p-2"
      style={{ backgroundColor: 'var(--w-scrim)' }}
      onClick={onClose}
    >
      <div
        data-testid="cart-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('waiter.ticket.cart.title')}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[480px] max-h-[85dvh] rounded-2xl bg-paper flex flex-col overflow-hidden"
      >
        <div className="shrink-0 h-[52px] flex items-center justify-between px-4 border-b-2 border-hairline">
          <span className="font-sans text-lg font-bold">{t('waiter.ticket.cart.title')}</span>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
            className="w-11 h-11 flex items-center justify-center font-sans text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto waiter-scroll p-1.5">
          {cartLines.map((line) => {
            const item = itemsById.get(line.itemId);
            const groups = item?.optionGroups ?? [];
            const subLines = resolveOptionSubLines(line, groups);
            const stale = staleById.get(line.lineId);
            return (
              <div
                key={line.lineId}
                data-testid={`cart-line-${line.lineId}`}
                className="border border-hairline rounded-lg px-2 py-1 mb-1.5"
              >
                {/* The name is the ONE edit control of a line — the old
                    app's blue text button. The ✎ that used to sit next to it
                    did the same thing twice (SC-842). */}
                <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                  <button
                    type="button"
                    data-testid={`cart-line-name-${line.lineId}`}
                    aria-label={t('waiter.ticket.cart.lineAria', { name: line.snapshot.name })}
                    onClick={() => onEditLine(line.lineId)}
                    className="min-h-11 px-0 py-1 text-start font-sans text-[18px] font-semibold min-w-0"
                    style={{ color: 'var(--w-info)' }}
                  >
                    {/* line-clamp sits on a span: a <button> ignores it. */}
                    <span className="leading-snug break-words line-clamp-3">
                      <DishCodeTag name={line.snapshot.name} code={line.snapshot.code} size="sm" />
                      {line.snapshot.name}
                    </span>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Same ghost-« − » fix as the Plats row — this sheet sits
                        on top of it, the two steppers must not disagree. */}
                    <button
                      type="button"
                      data-testid={`cart-line-minus-${line.lineId}`}
                      aria-label={t('menu.quickRemove', { name: line.snapshot.name })}
                      onClick={() => step(line, -1)}
                      className="w-11 h-11 border-2 border-ink rounded-lg bg-paper text-ink font-sans text-2xl font-bold leading-none"
                    >
                      −
                    </button>
                    <span
                      data-testid={`cart-line-qty-${line.lineId}`}
                      className="w-10 h-11 flex items-center justify-center border-2 border-hairline rounded-lg bg-paper text-ink font-mono font-bold text-xl tabular-nums"
                    >
                      {line.qty}
                    </span>
                    <button
                      type="button"
                      data-testid={`cart-line-plus-${line.lineId}`}
                      aria-label={t('menu.quickAdd', { name: line.snapshot.name })}
                      onClick={() => step(line, 1)}
                      className="w-11 h-11 rounded-lg font-sans text-2xl font-bold leading-none text-white"
                      style={{ backgroundColor: 'var(--ink)' }}
                    >
                      +
                    </button>
                  </div>
                </div>
                {subLines.map(({ optionId, text }) => (
                  <p
                    key={optionId}
                    data-testid={`cart-line-option-${line.lineId}-${optionId}`}
                    className="ps-4 mt-1 font-mono text-[15px] text-ink-mute"
                  >
                    {text}
                  </p>
                ))}
                {line.note && (
                  <p
                    data-testid={`cart-line-comment-${line.lineId}`}
                    className="ps-4 mt-1 font-mono text-[15px] italic text-ink-mute"
                  >
                    {line.note}
                  </p>
                )}
                {stale && (
                  <p
                    data-testid={`cart-line-stale-${line.lineId}`}
                    className="ps-4 mt-1 font-sans text-[15px] font-semibold"
                    style={{ color: 'var(--w-danger)' }}
                  >
                    {t(...staleLineMessage(stale))}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
